import { redactForProcessor } from './redact'

/**
 * Tracing and error tracking for the Workers runtime (design 16.7, G 16.7-09), with fetch only (no Node SDK):
 * - spans are exported as OTLP/HTTP JSON (`POST <OTEL_EXPORTER_OTLP_ENDPOINT>/v1/traces`, headers from
 *   OTEL_EXPORTER_OTLP_HEADERS as `k=v,k2=v2`), batched and flushed at the end of each request, job or alarm;
 * - exceptions go to a Sentry-compatible DSN (`SENTRY_DSN`) as envelopes (`POST /api/<project>/envelope/`).
 *
 * Telemetry leaves the region, so nothing personal goes with it: attribute values, span names, exception
 * messages and stack lines pass through the same redaction as logs (emails, phone numbers, card and bank
 * numbers, links), attribute keys are allow-listed by shape, and long values are cut. With neither setting,
 * every call is a cheap no-op.
 */

export interface TelemetryConfig {
  service: string
  environment: string
  region?: string | null
  release?: string | null
  otlpEndpoint?: string | null
  otlpHeaders?: string | null
  sentryDsn?: string | null
  fetchImpl?: typeof fetch
  /** Most spans kept per flush; the rest are counted and dropped. */
  maxSpans?: number
}

export type AttrValue = string | number | boolean | null | undefined
export type Attrs = Record<string, AttrValue>

export interface SpanContext { traceId: string; spanId: string }

interface SpanRecord {
  traceId: string; spanId: string; parentSpanId?: string; name: string; kind: number
  start: number; end: number; attrs: Attrs; status: { code: 0 | 1 | 2; message?: string }
}

/** OTLP span kinds. */
export const SpanKind = { internal: 1, server: 2, client: 3, producer: 4, consumer: 5 } as const

const hex = (bytes: number) => [...crypto.getRandomValues(new Uint8Array(bytes))].map((b) => b.toString(16).padStart(2, '0')).join('')
const MAX_VALUE = 300
const SAFE_KEY = /^[a-z][a-z0-9_.]{0,63}$/

/** A value safe to send out of the region: redacted, cut short; numbers and booleans as they are. */
export function safeAttr(v: AttrValue): AttrValue {
  if (typeof v === 'string') return redactForProcessor(v.slice(0, MAX_VALUE))
  return v
}
export function safeText(s: string, max = 500) { return redactForProcessor(String(s ?? '').slice(0, max)) }

function otlpValue(v: AttrValue) {
  if (typeof v === 'number') return Number.isInteger(v) ? { intValue: String(v) } : { doubleValue: v }
  if (typeof v === 'boolean') return { boolValue: v }
  return { stringValue: String(v ?? '') }
}
const otlpAttrs = (a: Attrs) => Object.entries(a).filter(([k, v]) => SAFE_KEY.test(k) && v !== undefined && v !== null).map(([key, value]) => ({ key, value: otlpValue(safeAttr(value)) }))
const nanos = (ms: number) => `${Math.round(ms * 1_000_000)}`

export function parseOtlpHeaders(s: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const part of String(s ?? '').split(',')) {
    const i = part.indexOf('=')
    if (i <= 0) continue
    try { out[decodeURIComponent(part.slice(0, i).trim())] = decodeURIComponent(part.slice(i + 1).trim()) } catch { /* skip a malformed pair */ }
  }
  return out
}

/** A parsed DSN: `https://<public key>@<host>/<project id>`. Null when it is not one. */
export function parseDsn(dsn: string | null | undefined): { key: string; envelopeUrl: string } | null {
  if (!dsn) return null
  try {
    const u = new URL(dsn)
    const project = u.pathname.replace(/^\/+|\/+$/g, '').split('/').pop()
    if (!u.username || !project || !/^\d+$/.test(project)) return null
    const prefix = u.pathname.replace(/\/+[^/]*\/?$/, '')
    return { key: u.username, envelopeUrl: `${u.protocol}//${u.host}${prefix}/api/${project}/envelope/` }
  } catch { return null }
}

export class Span {
  readonly spanId = hex(8)
  readonly traceId: string
  readonly parentSpanId?: string
  private ended = false
  private readonly startMs = Date.now()
  status: { code: 0 | 1 | 2; message?: string } = { code: 0 }
  constructor(private t: Telemetry, readonly name: string, readonly kind: number, readonly attrs: Attrs, parent?: SpanContext | null) {
    this.traceId = parent?.traceId ?? hex(16)
    this.parentSpanId = parent?.spanId
  }
  get context(): SpanContext { return { traceId: this.traceId, spanId: this.spanId } }
  set(key: string, v: AttrValue) { this.attrs[key] = v; return this }
  fail(e: unknown) { this.status = { code: 2, message: safeText(e instanceof Error ? `${e.name}: ${e.message}` : String(e), 200) }; return this }
  end() {
    if (this.ended) return
    this.ended = true
    this.t._record({ traceId: this.traceId, spanId: this.spanId, parentSpanId: this.parentSpanId, name: safeText(this.name, 120), kind: this.kind, start: this.startMs, end: Date.now(), attrs: this.attrs, status: this.status.code ? this.status : { code: 1 } })
  }
}

export class Telemetry {
  private spans: SpanRecord[] = []
  private dropped = 0
  private events: string[] = []
  private readonly headers: Record<string, string>
  private readonly sentry: ReturnType<typeof parseDsn>
  private readonly f: typeof fetch
  readonly tracing: boolean
  readonly errors: boolean
  constructor(readonly cfg: TelemetryConfig) {
    this.headers = parseOtlpHeaders(cfg.otlpHeaders)
    this.sentry = parseDsn(cfg.sentryDsn)
    this.tracing = Boolean(cfg.otlpEndpoint)
    this.errors = Boolean(this.sentry)
    this.f = cfg.fetchImpl ?? ((i, init) => fetch(i, init))
  }
  get enabled() { return this.tracing || this.errors }

  startSpan(name: string, attrs: Attrs = {}, parent?: SpanContext | null, kind: number = SpanKind.internal) {
    return new Span(this, name, kind, { ...attrs }, parent)
  }

  /** Runs fn in a span: ended when fn settles, marked failed (and the error reported) when it throws. */
  async span<T>(name: string, attrs: Attrs, fn: (s: Span) => Promise<T>, parent?: SpanContext | null, kind: number = SpanKind.internal): Promise<T> {
    if (!this.enabled) return fn(NOOP_SPAN)
    const s = this.startSpan(name, attrs, parent, kind)
    try { return await fn(s) } catch (e) { s.fail(e); this.captureException(e, { tags: { span: name }, trace: s.context }); throw e } finally { s.end() }
  }

  _record(r: SpanRecord) {
    if (!this.tracing) return
    if (this.spans.length >= (this.cfg.maxSpans ?? 500)) { this.dropped++; return }
    this.spans.push(r)
  }

  /** Reports an exception to the DSN (redacted; no request bodies, no customer text). */
  captureException(e: unknown, ctx: { tags?: Record<string, string | number | boolean | null | undefined>; trace?: SpanContext | null; level?: 'error' | 'warning' | 'fatal' } = {}) {
    if (!this.sentry) return
    const err = e instanceof Error ? e : new Error(String(e))
    const frames = String(err.stack ?? '').split('\n').slice(1, 30).map((l) => {
      const m = l.match(/at (?:(.+?) )?\(?([^()]+?):(\d+):(\d+)\)?$/)
      return m ? { function: safeText(m[1] ?? '?', 120), filename: safeText(m[2]!, 200), lineno: Number(m[3]), colno: Number(m[4]), in_app: !/node_modules/.test(m[2]!) } : null
    }).filter(Boolean).reverse()
    const eventId = hex(16)
    const tags = Object.fromEntries(Object.entries(ctx.tags ?? {}).filter(([k, v]) => SAFE_KEY.test(k) && v !== undefined && v !== null).map(([k, v]) => [k, String(safeAttr(v as AttrValue))]))
    const event = {
      event_id: eventId, timestamp: Date.now() / 1000, platform: 'javascript', level: ctx.level ?? 'error', logger: this.cfg.service,
      environment: this.cfg.environment, release: this.cfg.release ?? undefined, server_name: this.cfg.region ? `${this.cfg.service}-${this.cfg.region}` : this.cfg.service,
      tags: { service: this.cfg.service, region: this.cfg.region ?? undefined, ...tags },
      exception: { values: [{ type: safeText(err.name, 80), value: safeText(err.message), stacktrace: frames.length ? { frames } : undefined }] },
      contexts: ctx.trace ? { trace: { trace_id: ctx.trace.traceId, span_id: ctx.trace.spanId } } : undefined,
    }
    const header = { event_id: eventId, sent_at: new Date().toISOString(), sdk: { name: 'takeit4me.telemetry', version: '1.0.0' } }
    this.events.push(`${JSON.stringify(header)}\n${JSON.stringify({ type: 'event', content_type: 'application/json' })}\n${JSON.stringify(event)}\n`)
  }

  /** Sends what was collected. Never throws: telemetry must not break the work it watches. */
  async flush(): Promise<void> {
    const spans = this.spans.splice(0)
    const events = this.events.splice(0)
    const dropped = this.dropped
    this.dropped = 0
    const sends: Promise<unknown>[] = []
    if (spans.length && this.cfg.otlpEndpoint) {
      const resource = { attributes: otlpAttrs({ 'service.name': this.cfg.service, 'deployment.environment': this.cfg.environment, 'cloud.region': this.cfg.region ?? undefined, 'service.version': this.cfg.release ?? undefined, 'telemetry.dropped_spans': dropped || undefined }) }
      const body = {
        resourceSpans: [{ resource, scopeSpans: [{ scope: { name: 'takeit4me', version: '1' }, spans: spans.map((s) => ({
          traceId: s.traceId, spanId: s.spanId, ...(s.parentSpanId ? { parentSpanId: s.parentSpanId } : {}), name: s.name, kind: s.kind,
          startTimeUnixNano: nanos(s.start), endTimeUnixNano: nanos(Math.max(s.end, s.start)), attributes: otlpAttrs(s.attrs), status: s.status,
        })) }] }],
      }
      const url = `${this.cfg.otlpEndpoint.replace(/\/+$/, '')}${/\/v1\/traces$/.test(this.cfg.otlpEndpoint) ? '' : '/v1/traces'}`
      sends.push(this.f(url, { method: 'POST', headers: { 'content-type': 'application/json', ...this.headers }, body: JSON.stringify(body), signal: AbortSignal.timeout(5_000) }))
    }
    if (events.length && this.sentry) {
      const auth = `Sentry sentry_version=7, sentry_key=${this.sentry.key}, sentry_client=takeit4me.telemetry/1.0.0`
      for (const env of events) sends.push(this.f(this.sentry.envelopeUrl, { method: 'POST', headers: { 'content-type': 'application/x-sentry-envelope', 'x-sentry-auth': auth }, body: env, signal: AbortSignal.timeout(5_000) }))
    }
    const results = await Promise.allSettled(sends)
    for (const r of results) if (r.status === 'rejected') console.warn('telemetry export failed', String(r.reason).slice(0, 120))
  }
}

const NOOP_SPAN = new Proxy({} as Span, { get: (_t, k) => (k === 'context' ? null : k === 'set' || k === 'fail' ? () => NOOP_SPAN : k === 'end' ? () => {} : undefined) })

/** Telemetry from a Worker or Node environment: OTEL_EXPORTER_OTLP_ENDPOINT, OTEL_EXPORTER_OTLP_HEADERS, SENTRY_DSN. */
export function telemetryFromEnv(env: Record<string, unknown>, service: string): Telemetry {
  const s = (k: string) => (typeof env[k] === 'string' && (env[k] as string).trim() ? (env[k] as string).trim() : null)
  return new Telemetry({
    service, environment: s('ENVIRONMENT') ?? 'development', region: s('REGION'), release: s('RELEASE') ?? s('CF_VERSION_METADATA_ID'),
    otlpEndpoint: s('OTEL_EXPORTER_OTLP_ENDPOINT'), otlpHeaders: s('OTEL_EXPORTER_OTLP_HEADERS'), sentryDsn: s('SENTRY_DSN'),
  })
}
